import React, { useEffect, useMemo, useState } from 'react';
import StatTile from '../ui/StatTile';
import SortableTable from '../SortableTable';
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

const formatYMD = (d) => new Date(d).toISOString().split('T')[0];

const formatMaybeYMD = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toISOString().split('T')[0];
  } catch {
    return String(d);
  }
};

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const isInternalClient = (clientName) => {
  if (!clientName) return false;
  const upper = clientName.toUpperCase();
  return upper.includes('GLOBE') || upper.includes('PRIMER IMPACTO');
};

const StatisticsClientsOverview = ({ startDate, endDate, toolFilter, fetchWithAuth, apiBaseUrl, includeInternal }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [installedData, setInstalledData] = useState([]);
  const [loadingInstalled, setLoadingInstalled] = useState(false);

  const shouldShowInstalledClientsGraph = useMemo(() => {
    return !toolFilter?.value;
  }, [toolFilter]);

  useEffect(() => {
    const load = async () => {
      if (!endDate) { setRows([]); return; }
      setLoading(true);
      try {
        const params = new URLSearchParams({ endDate: formatYMD(endDate) });
        const resp = await fetchWithAuth(`${apiBaseUrl}/api/dwh/bi/tools/stats/clients-overview?${params.toString()}`);
        if (!resp.ok) { setRows([]); return; }
        const json = await resp.json();
        setRows(json.data || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [endDate, fetchWithAuth, apiBaseUrl]);

  useEffect(() => {
    const loadInstalled = async () => {
      if (!startDate || !endDate || !shouldShowInstalledClientsGraph) { setInstalledData([]); return; }
      setLoadingInstalled(true);
      try {
        const params = new URLSearchParams({ startDate: formatYMD(startDate), endDate: formatYMD(endDate) });
        const resp = await fetchWithAuth(`${apiBaseUrl}/api/dwh/bi/tools/stats/clients-installed-evolution?${params.toString()}`);
        if (!resp.ok) { setInstalledData([]); return; }
        const json = await resp.json();
        setInstalledData(json.data || []);
      } finally {
        setLoadingInstalled(false);
      }
    };
    loadInstalled();
  }, [startDate, endDate, shouldShowInstalledClientsGraph, fetchWithAuth, apiBaseUrl]);

  const tableData = useMemo(() => {
    let filteredRows = rows || [];
    if (!includeInternal) {
      filteredRows = filteredRows.filter(r => !isInternalClient(r.client || r.CLIENT || r['client']));
    }

    return filteredRows.map((r) => ({
      client: r.client || r.CLIENT || r['client'],
      user_count: toNumber(r.user_count ?? r.USER_COUNT ?? r['user_count']),
      sources: r.sources || r.SOURCES || r['sources'] || '',
      last_visit_date: r.last_visit_date || r.LAST_VISIT_DATE || r['last_visit_date'] || null,
      last_active_username: r.last_active_username || r.LAST_ACTIVE_USERNAME || r['last_active_username'] || ''
    }));
  }, [rows, includeInternal]);

  const installedChartData = useMemo(() => {
    if (!installedData.length) return [];
    return installedData
      .map((row) => {
        const period = row.period || row.PERIOD;
        if (!period) return null;
        const d = new Date(period);
        if (isNaN(d.getTime())) return null;
        const key = d.toISOString().split('T')[0];
        return {
          period: key,
          equippedClients: Number(row.equipped_clients ?? row.EQUIPPED_CLIENTS ?? 0) || 0,
          knownClients: Number(row.known_clients ?? row.KNOWN_CLIENTS ?? 0) || 0
        };
      })
      .filter(Boolean)
      .sort((a, b) => new Date(a.period) - new Date(b.period));
  }, [installedData]);

  const installedYAxisMax = useMemo(() => {
    if (!installedChartData.length) return 0;
    let max = 0;
    for (const r of installedChartData) {
      max = Math.max(max, Number(r.equippedClients) || 0, Number(r.knownClients) || 0);
    }
    return max;
  }, [installedChartData]);

  const totals = useMemo(() => {
    const knownClients = tableData.length;
    const clientsWithAccess = tableData.filter((r) => (r.user_count || 0) > 0).length;
    const equippedPct = knownClients > 0 ? (clientsWithAccess / knownClients) * 100 : 0;
    const totalUsersEquipped = tableData.reduce((acc, r) => acc + ((r.user_count || 0) > 0 ? (r.user_count || 0) : 0), 0);
    const avgUsersPerEquippedClient = clientsWithAccess > 0 ? totalUsersEquipped / clientsWithAccess : 0;
    return { knownClients, clientsWithAccess, equippedPct, avgUsersPerEquippedClient };
  }, [tableData]);

  const columns = useMemo(() => ([
    { key: 'client', label: 'Client' },
    { key: 'user_count', label: 'Utilisateurs' },
    {
      key: 'last_visit_date',
      label: 'Dernière visite',
      render: (r) => formatMaybeYMD(r.last_visit_date),
      rawValue: (r) => formatMaybeYMD(r.last_visit_date)
    },
    { key: 'last_active_username', label: 'Utilisateur (dernier actif)' },
    { key: 'sources', label: 'Sources' }
  ]), []);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatTile
          label="Clients connus (base)"
          value={totals.knownClients.toLocaleString('fr-FR')}
          className="text-2xl font-bold text-blue-800 dark:text-blue-200"
          tileClassName="bg-blue-50 dark:bg-blue-900/20"
        />
        <StatTile
          label="Clients avec accès"
          value={totals.clientsWithAccess.toLocaleString('fr-FR')}
          className="text-2xl font-bold text-green-800 dark:text-green-200"
          tileClassName="bg-green-50 dark:bg-green-900/20"
        />
        <StatTile
          label="% clients équipés"
          value={`${totals.equippedPct.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}%`}
          className="text-2xl font-bold text-purple-800 dark:text-purple-200"
          tileClassName="bg-purple-50 dark:bg-purple-900/20"
        />
        <StatTile
          label="Moy. users / client équipé"
          value={totals.avgUsersPerEquippedClient.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}
          className="text-2xl font-bold text-orange-800 dark:text-orange-200"
          tileClassName="bg-orange-50 dark:bg-orange-900/20"
        />
      </div>

      {shouldShowInstalledClientsGraph && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold dark:text-white">Évolution des comptes clients installés</h3>
            <div className="text-sm text-gray-500">
              Cumul (clients avec au moins 1 compte)
            </div>
          </div>

          {loadingInstalled ? (
            <div className="text-gray-500 text-center py-8">Chargement...</div>
          ) : installedChartData.length === 0 ? (
            <div className="text-gray-500 text-center py-8">Aucune donnée disponible pour cette période.</div>
          ) : (
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={installedChartData} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="period"
                    tickFormatter={(val) => {
                      const d = new Date(val);
                      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
                    }}
                  />
                  <YAxis allowDecimals={false} domain={[0, installedYAxisMax]} />
                  <Tooltip
                    labelFormatter={(label) => new Date(label).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    formatter={(value, name) => [value, name]}
                    cursor={{ fill: 'rgba(0,0,0,0.05)' }}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="equippedClients" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.18} strokeWidth={2} name="Clients équipés" />
                  <Area type="monotone" dataKey="knownClients" stroke="#94a3b8" fill="#94a3b8" fillOpacity={0.10} strokeWidth={2} name="Clients connus (base)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      <div className="p-4 rounded-lg border dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold dark:text-white">Clients & comptes utilisateurs (au {formatYMD(endDate)})</h3>
        </div>
        <SortableTable
          data={tableData}
          columns={columns}
          emptyMessage={loading ? 'Chargement...' : 'Aucune donnée'}
          countLabel={`Clients: ${tableData.length}`}
          exportable={true}
          exportFilename={`clients_overview_${formatYMD(endDate)}.csv`}
          disableFilters={false}
          initialSortBy={{ key: 'user_count', direction: 'descending' }}
        />
      </div>
    </div>
  );
};

export default StatisticsClientsOverview;


